import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchArticles } from '@/utils/articles';

interface SearchBoxProps {
  variant?: 'desktop' | 'mobile';
  onNavigate?: () => void;
}

export default function SearchBox({ variant = 'desktop', onNavigate }: SearchBoxProps) {
  const [keyword, setKeyword] = useState('');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef<HTMLDivElement>(null);

  const suggestions = keyword.trim() ? searchArticles(keyword).slice(0, 6) : [];

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const goSearch = (q: string) => {
    const value = q.trim();
    if (!value) return;
    setOpen(false);
    onNavigate?.();
    navigate(`/search?q=${encodeURIComponent(value)}`);
  };

  const goDetail = (id: string) => {
    setOpen(false);
    onNavigate?.();
    navigate(`/detail/${id}`);
  };

  return (
    <div ref={boxRef} className="relative w-full">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          goSearch(keyword);
        }}
        className={`flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-3 ${
          variant === 'mobile' ? 'h-10' : 'h-9'
        }`}
      >
        <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
          <i className="ri-search-line text-base"></i>
        </span>
        <input
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="搜索黑料 / 明星 / 热点…"
          className="h-full w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
        />
        <button
          type="submit"
          className="shrink-0 cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          搜索
        </button>
      </form>

      {open && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-11 z-50 animate-fade-in overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1 shadow-lg">
          {suggestions.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => goDetail(item.id)}
              className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-background-100"
            >
              <i className="ri-search-eye-line text-sm text-primary-500"></i>
              <span className="clamp-1 flex-1 text-sm text-foreground-800">{item.title}</span>
              <span className="shrink-0 text-xs text-foreground-400">{item.category}</span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => goSearch(keyword)}
            className="flex w-full cursor-pointer items-center justify-center gap-1 border-t border-background-200 px-3 py-2 text-xs font-medium text-primary-600 transition-colors hover:bg-background-100"
          >
            查看全部搜索结果
            <i className="ri-arrow-right-s-line text-sm"></i>
          </button>
        </div>
      )}
    </div>
  );
}